import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, PageBody, PageHeader, Well } from "../ui/Field";

type Site = { id: string; slug: string; name: string; default_locale: string; role: string };
type PageRow = { id: string; slug: string; title: string; status: string; updated_at: number };
type BuildRow = { id: string; status: string; finished_at: number | null; pages_deployment_id: string | null };

export default function SiteDashboard() {
  const { slug } = useParams();
  const [site, setSite] = useState<Site | null>(null);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [builds, setBuilds] = useState<BuildRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      api.get<Site>(`/api/sites/${slug}`),
      api.get<{ items: PageRow[] }>(`/api/sites/${slug}/pages`),
      api.get<{ items: BuildRow[] }>(`/api/sites/${slug}/builds`).catch(() => ({ items: [] })),
    ])
      .then(([s, p, b]) => { setSite(s); setPages(p.items); setBuilds(b.items.slice(0, 5)); })
      .catch((e) => setErr((e as Error).message));
  }, [slug]);

  if (err) return <PageBody><Banner kind="error">{err}</Banner></PageBody>;
  if (!site) return <PageBody><div className="h-16 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;

  const publishedPages = pages.filter((p) => p.status === "published" && !p.slug.startsWith("__"));
  const draftPages = pages.filter((p) => p.status === "draft" && !p.slug.startsWith("__"));
  const lastBuild = builds[0];

  return (
    <PageBody>
      <PageHeader
        eyebrow={`№ ${site.slug.toUpperCase()}`}
        title={site.name}
        description={<>Site overview · <span className="font-mono">{site.role}</span> · locale <span className="font-mono">{site.default_locale}</span></>}
        actions={
          <>
            <Link to={`/sites/${slug}/pages`} className="inline-flex h-9 items-center px-3 rounded-[var(--radius-sm)] border border-[var(--color-rule)] text-[13px] no-underline text-[var(--color-ink)] hover:bg-[var(--color-paper-2)] transition-colors">Pages</Link>
            <Link to={`/sites/${slug}/builds`} className="inline-flex h-9 items-center px-4 rounded-[var(--radius-sm)] bg-[var(--color-accent)] text-[var(--color-paper)] text-[13.5px] font-medium no-underline hover:bg-[var(--color-accent-2)] transition-colors">
              Publish ↗
            </Link>
          </>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Tile label="Pages" value={pages.length} sub={`${publishedPages.length} live · ${draftPages.length} draft`} />
        <Tile label="Builds" value={builds.length} sub={lastBuild ? `Last: ${lastBuild.status}` : "None yet"} />
        <Tile label="Locale" value={site.default_locale} sub="default" />
        <Tile label="Status" value="Active" sub={lastBuild?.pages_deployment_id ? "Deployed" : "Awaiting publish"} accent={!lastBuild?.pages_deployment_id} />
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card
          number="01"
          title="Recent pages"
          actions={<Link to={`/sites/${slug}/pages`} className="text-[12px] text-[var(--color-accent)] hover:text-[var(--color-accent-2)]">All →</Link>}
          className="md:col-span-2"
          bodyClassName="p-0"
        >
          {pages.length === 0 ? (
            <div className="p-6"><EmptyState title="No pages yet." description="Start by creating your home page." action={<Link to={`/sites/${slug}/pages`}><Button>Open pages</Button></Link>} /></div>
          ) : (
            <ul className="divide-y divide-[var(--color-rule)] px-5">
              {pages.slice(0, 7).map((p) => (
                <li key={p.id}>
                  <Link to={`/sites/${slug}/pages/${p.id}`} className="flex items-center justify-between gap-4 py-3 no-underline group">
                    <span className="min-w-0 flex-1">
                      <span className="text-[14px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors truncate block">{p.title}</span>
                      <span className="font-mono text-[11px] text-[var(--color-muted)]">/{p.slug}</span>
                    </span>
                    <Badge tone={p.status === "published" ? "success" : p.status === "scheduled" ? "info" : p.status === "review" ? "warning" : "neutral"}>{p.status}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card number="02" title="Quick actions" bodyClassName="p-0">
          <ul className="divide-y divide-[var(--color-rule)]">
            <QuickAction to={`/sites/${slug}/pages`} title="Edit content" hint="Pages, blocks" />
            <QuickAction to={`/sites/${slug}/assets`} title="Upload assets" hint="Images, files" />
            <QuickAction to={`/sites/${slug}/theme`} title="Theme tokens" hint="Colors, fonts" />
            <QuickAction to={`/sites/${slug}/members`} title="Invite team" hint="Roles & access" />
            <QuickAction to={`/sites/${slug}/builds`} title="Publish" hint="Deploy to Pages" />
            <QuickAction to={`/sites/${slug}/settings`} title="Settings" hint="SEO, PWA, CSP" />
          </ul>
        </Card>
      </div>

      <Well className="mt-8">
        <div className="flex items-start gap-3">
          <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--color-muted)]">URL</span>
          <span className="flex-1 font-mono text-[13px] text-[var(--color-ink-2)]">
            {site.role && `cmsplus-${site.slug}.pages.dev`}
          </span>
        </div>
      </Well>
    </PageBody>
  );
}

function Tile({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: boolean }) {
  return (
    <div className={`border ${accent ? "border-[var(--color-accent)]/40 bg-[var(--color-accent)]/[0.04]" : "border-[var(--color-rule)] bg-[var(--color-surface)]"} rounded-[var(--radius-sm)] px-4 py-3`}>
      <div className="label-tracked">{label}</div>
      <div className="display text-3xl text-[var(--color-ink)] leading-none mt-1.5">{value}</div>
      {sub && <div className="text-[11.5px] text-[var(--color-muted)] mt-1.5 font-mono">{sub}</div>}
    </div>
  );
}

function QuickAction({ to, title, hint }: { to: string; title: string; hint: string }) {
  return (
    <li>
      <Link to={to} className="flex items-center justify-between px-5 py-3 no-underline hover:bg-[var(--color-paper-2)] group transition-colors">
        <span>
          <span className="block text-[13.5px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors">{title}</span>
          <span className="block text-[11px] text-[var(--color-muted)] mt-0.5">{hint}</span>
        </span>
        <span className="text-[var(--color-mute-2)] group-hover:text-[var(--color-accent)] transition-colors">→</span>
      </Link>
    </li>
  );
}
